/*!
 * live.js — Oscilla Live: the console on an empty stage
 * © 2026 Rob Canning — GPLv3
 *
 * The second entry page beside app.js (the score). It loads the engine --
 * the cue language, sound, shapes, viz, text -- and the live console, and
 * none of the score app: no project, transport, notation editor or server.
 * Any static file host can serve it.
 *
 * test/moduleBoundaries.test.js holds it to that: what this file loads may
 * not reach a score-app module.
 */

import { initLiveConsole, showLiveConsole, setLivePerform, setBufferName, bufferText, switchBuffer, runAllLines, isLiveConsoleOpen, toggleLiveKeys, isLiveKeysOpen } from "./interaction/oscillaLive.js";
import { installKeyHandling } from "./system/keyLayers.js";
import { setMidiAutoOpen } from "./control/midiIn.js";
import { initRoomPanel } from "./interaction/liveRoomPanel.js";
import { startClockLink } from "./interaction/liveClockLink.js";
import { initLanding } from "./interaction/liveLanding.js";
import { useLiveDocs } from "./interaction/liveDocs.js";
import { useLiveMedia } from "./interaction/liveMedia.js";
import { pickFolder, dropFolder, carriesFiles, describeFolder } from "./system/mediaFolder.js";
import { projectFromAddress, useProjectFile } from "./interaction/liveProject.js";
import { randomExample, exampleCode, listExamples, clearStage } from "./interaction/liveExamples.js";
import { registerEscapeLayer } from "./system/keyLayers.js";
import { sceneStatus } from "./interaction/liveScenes.js";
import { handleCueTrigger } from "./cues/cueDispatcher.js";
import { setDarkStage, isDarkStage } from "./system/darkStage.js";
import { initBackgroundClock } from "./system/backgroundClock.js";
import { fromAddress, codeFrom, sessionWith, bufferNameOf, sessionAddress, touchSession, shareAddress } from "./interaction/liveSessions.js";
import "./cues/audio/index.js";
import "./cues/synth.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * The stage: an empty score the size of the window, in the place the cues
 * look for one (#scoreContainer > #scrollStage svg).
 */
const FOLLOWER = new URLSearchParams(location.search).get("follow") === "1";

function makeStage() {
  const container = document.getElementById("scoreContainer");
  const stage = document.createElement("div");
  stage.id = "scrollStage";
  stage.dataset.liveStage = "1";      // the window's own stage, not a score's that scrolls (viz.js)
  const inner = document.createElement("div");
  inner.id = "scoreInner";
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${innerWidth} ${innerHeight}`);
  inner.appendChild(svg);
  stage.appendChild(inner);
  container.appendChild(stage);
  window.scoreContainer = container;
}

/**
 * share: a link with the session's code in it, copied (liveSessions.js).
 * Where the clipboard is refused, the link is shown to copy by hand.
 */
function initShare() {
  const btn = document.querySelector("#live-room .live-room-share");
  const note = document.querySelector("#live-room .live-room-note");
  btn?.addEventListener("click", async () => {
    const link = await shareAddress(bufferText());
    const kb = (link.length / 1024).toFixed(1);
    try {
      await navigator.clipboard.writeText(link);
      note.textContent = `link copied (${kb} KB): it opens this code as a session of its own`;
    } catch {
      note.textContent = link;
    }
    clearTimeout(note._t);
    note._t = setTimeout(() => { note.textContent = ""; }, 6000);
  });
}

/**
 * Your own files (mediaFolder.js): the folder icon asks for a folder, and a
 * folder dropped anywhere on the page is taken. Nothing leaves the browser.
 * What was loaded is said in the console's output, and on the landing.
 */
function initFolder() {
  const say = said => { if (said) window.oscillaLiveLog?.(said, "ok"); };
  const fail = err => window.oscillaLiveLog?.(`folder: ${err.message}`, "err");
  document.querySelector("#live-room .live-room-folder")?.addEventListener("click", () => pickFolder().then(say, fail));
  window.addEventListener("oscilla:media-folder", () => {
    const now = document.querySelector("#live-folder .live-folder-now");
    if (now) now.textContent = describeFolder();
  });
  // dragenter and dragleave come for every element crossed: counted
  let depth = 0;
  const show = on => document.body.classList.toggle("live-dropping", on);
  window.addEventListener("dragenter", e => { if (carriesFiles(e)) { depth++; show(true); } });
  window.addEventListener("dragleave", e => { if (carriesFiles(e) && --depth <= 0) { depth = 0; show(false); } });
  window.addEventListener("dragover", e => { if (carriesFiles(e)) e.preventDefault(); });
  window.addEventListener("drop", e => {
    if (!carriesFiles(e)) return;
    e.preventDefault();               // not opened as a page, nor typed into the code
    depth = 0;
    show(false);
    dropFolder(e.dataTransfer).then(say, fail);
  });
}

/**
 * The corner's list of the examples, by name: one picked opens as a fresh
 * session (pick). Made on first opening; shut by a pick, Esc, or a click
 * outside it.
 */
function initExampleList(pick) {
  const box = document.getElementById("live-room");
  const btn = box?.querySelector(".live-room-examples");
  const panel = box?.querySelector(".live-room-examples-panel");
  if (!btn || !panel) return;
  const isOpen = () => box.classList.contains("examples-open");
  const show = async on => {
    box.classList.toggle("examples-open", on);
    btn.setAttribute("aria-expanded", on ? "true" : "false");
    if (!on || panel.childElementCount) return;
    const list = await listExamples();
    panel.replaceChildren(...list.map(e => {
      const b = document.createElement("button");
      b.type = "button";
      const t = document.createElement("span"); t.className = "t"; t.textContent = e.title;
      const a = document.createElement("span"); a.className = "a"; a.textContent = e.about;
      b.title = e.about;
      b.append(t, a);
      b.addEventListener("click", () => { show(false); pick(e.file); });
      return b;
    }));
  };
  btn.addEventListener("click", () => show(!isOpen()));
  document.addEventListener("pointerdown", e => { if (isOpen() && !box.contains(e.target)) show(false); }, true);
  registerEscapeLayer({ id: "live-examples", tier: "popup", isActive: isOpen, dismiss: () => show(false) });
  return () => { if (isOpen()) show(false); };
}

/**
 * The corner's list of the buffer's scenes (#$ name blocks, liveScenes.js): the
 * one on marked, a change under way as a bar, a sequence and how long its scene
 * has left. A click goes to one, fading over four beats; Shift+click cuts.
 * Read again four times a second while open.
 */
function initSceneList() {
  const box = document.getElementById("live-room");
  const btn = box?.querySelector(".live-room-scenes");
  const panel = box?.querySelector(".live-room-scenes-panel");
  if (!btn || !panel) return;
  let timer = 0;
  const isOpen = () => box.classList.contains("scenes-open");
  const draw = () => {
    const s = sceneStatus();
    const rows = s.names.map(name => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = name === s.current ? "on" : "";
      b.textContent = name;
      if (s.changing?.to === name) b.style.setProperty("--through", `${Math.round(s.changing.t * 100)}%`);
      b.title = "go to it, fading over 4 beats (Shift: a cut)";
      b.addEventListener("click", e => handleCueTrigger(`scene(${name}${e.shiftKey ? ", cut" : ", fade:4"})`, false, true, null, { live: true }));
      return b;
    });
    const foot = document.createElement("p");
    foot.className = "foot";
    foot.textContent = !s.names.length ? "no scenes: a block under #$ name is one"
      : s.sequence ? `sequence: ${s.sequence.steps.join(" -> ")}${s.sequence.loop ? " (round)" : ""} -- ${Math.ceil(s.sequence.beatsLeft)} beats left`
      : "click: fade over 4 beats -- Shift+click: a cut";
    panel.replaceChildren(...rows, foot);
  };
  const show = on => {
    box.classList.toggle("scenes-open", on);
    btn.setAttribute("aria-expanded", on ? "true" : "false");
    clearInterval(timer);
    if (on) { draw(); timer = setInterval(draw, 250); }
  };
  btn.addEventListener("click", () => show(!isOpen()));
  document.addEventListener("pointerdown", e => { if (isOpen() && !box.contains(e.target)) show(false); }, true);
  registerEscapeLayer({ id: "live-scenes", tier: "popup", isActive: isOpen, dismiss: () => show(false) });
  return () => { if (isOpen()) show(false); };
}

/**
 * The corner's popups one at a time: a click on any of its icons puts away
 * the others before its own opens (or before it acts). Run in the capture
 * phase, ahead of the icon's own handler. closers: [selector of the icon
 * that owns the popup, put it away].
 */
function initCornerPopups(closers) {
  document.querySelector("#live-room .live-room-bar")?.addEventListener("click", e => {
    const btn = e.target.closest("button");
    if (!btn) return;
    for (const [owner, close] of closers) if (!btn.matches(owner)) close();
  }, true);
}

/**
 * home: back to the landing (the examples, the sessions kept here, a room to
 * join). The address without its session -- nor the room or project that
 * would open one straight away. The buffer is kept on the way out
 * (oscillaLive.js, beforeunload), so it is first under Continue.
 */
function initHome() {
  document.querySelector("#live-room .live-room-home")?.addEventListener("click", () => {
    const q = new URLSearchParams(location.search);
    q.delete("room");
    q.delete("project");
    const search = q.toString();
    location.assign(`${location.pathname}${search ? `?${search}` : ""}`);
  });
}

/**
 * The corner's icons pinned by default: always there. The pin unpins them --
 * out of sight until the pointer comes to the corner (live.css) -- kept in
 * this browser.
 */
const PIN_KEY = "oscilla.livePinned";
function initPin() {
  const box = document.getElementById("live-room");
  const btn = box?.querySelector(".live-room-pin");
  if (!btn) return;
  const set = on => {
    box.classList.toggle("pinned", on);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.title = on ? "unpin: the icons out of sight until the pointer comes to the corner" : "pin: the icons always there";
  };
  let pinned = true;
  try { pinned = localStorage.getItem(PIN_KEY) !== "0"; } catch { /* private mode */ }
  set(pinned);
  btn.addEventListener("click", () => {
    const on = !box.classList.contains("pinned");
    set(on);
    try { localStorage.setItem(PIN_KEY, on ? "1" : "0"); } catch { /* private mode */ }
  });
}

/**
 * The stage dark by default (Oscilla's dark mode: the page inverted, the
 * console's layers and Live's chrome, pictures and film turned back); the
 * corner's light / dark switches it, kept in this browser.
 */
const DARK_KEY = "oscilla.liveDark";
function initDark() {
  let dark = true;
  try { dark = localStorage.getItem(DARK_KEY) !== "0"; } catch { /* private mode */ }
  setDarkStage(dark);
  const btn = document.querySelector("#live-room .live-room-dark");
  // an icon: a sun to make it light, a moon to make it dark (data-dark, in the CSS)
  const label = () => {
    if (!btn) return;
    const dark = isDarkStage(), word = dark ? "light stage" : "dark stage";
    btn.dataset.dark = dark ? "1" : "0";
    btn.title = word;
    btn.setAttribute("aria-label", word);
  };
  label();
  btn?.addEventListener("click", () => {
    const on = !isDarkStage();
    setDarkStage(on);
    try { localStorage.setItem(DARK_KEY, on ? "1" : "0"); } catch { /* private mode */ }
    label();
  });
}

document.addEventListener("DOMContentLoaded", async () => {
  makeStage();
  initDark();
  initPin();
  installKeyHandling();          // Escape and perform mode; no score keymap
  initBackgroundClock();         // hidden or covered, the beats, motions and tweens go on
  setMidiAutoOpen(true);         // a trig:midi line opens this browser's MIDI devices
  initLiveConsole({ restore: false });
  useLiveDocs();                 // Ctrl+Shift+D: Live's own docs, not a score's
  initShare();
  initFolder();
  // its own folder of pictures, sounds, film and text, on Oscilla's server
  // ... or a project's (?project=flowstates): its media, and its code in text/livecode.txt
  const project = projectFromAddress();
  const mediaDir = await useLiveMedia(project);
  const onProject = project && window.currentProjectName === project;

  // a shared link (#code=): its code becomes a session of its own, and the
  // address that session's -- a reload must not make another
  const asked = fromAddress();
  // a project opens straight on its own code (another session named in the address stays that one)
  if (onProject) {
    const id = await useProjectFile(project);
    if (!asked.code && !asked.session) asked.session = id;
  }
  if (asked.code) {
    asked.session = sessionWith((await codeFrom(asked.code)) ?? "// this link's code could not be read");
    history.replaceState(null, "", sessionAddress(asked.session));
  }

  let current = null;
  const rooms = initRoomPanel({
    onJoined: room => { touchSession(current, room); history.replaceState(null, "", sessionAddress(current, room)); },
    onLeft: () => { touchSession(current, null); history.replaceState(null, "", sessionAddress(current)); },
  });
  // with no room, on a page Oscilla's server serves (its relay is this host's):
  // the server's clock alone, so Ableton Link (--link) reaches a solo set too.
  // Anywhere else it is refused or unanswered, once, quietly (liveClockLink.js)
  if ((document.querySelector('meta[name="oscilla-relay"]')?.content?.trim() || "this-host") === "this-host") startClockLink();

  // the console on a session (its own buffer), the code over the whole stage
  // (Ctrl+Shift+F leaves it) -- after the landing: perform mode takes every
  // key, and the landing's form needs them
  const open = (id, room) => {
    current = id;
    setBufferName(bufferNameOf(id));
    touchSession(id, room);
    history.replaceState(null, "", sessionAddress(id, room));
    // a projector window (projector(), ?follow=1) has no console of its own:
    // it shows the code of each laptop that puts it up (oscillaLive.js applyView)
    if (!FOLLOWER) {
      showLiveConsole();
      setLivePerform(true);
    }
    // in a room: back in with this tab's password, or asked for it
    if (room && rooms) {
      const pw = rooms.kept(room);
      if (pw) rooms.join(room, pw, rooms.name);
      else rooms.ask(room);
    }
  };
  // an example -- at random, or the one picked from the list: a session of its
  // own (the example as it is on the server now), the stage cleared, every line
  // run (from the landing, or the corner's buttons over a session)
  const play = async code => {
    if (code == null) return;
    await clearStage();
    const id = sessionWith(code);
    if (isLiveConsoleOpen()) {
      // over a session: this one next, in the room it is in (still joined)
      const room = new URLSearchParams(location.search).get("room");
      current = id;
      switchBuffer(bufferNameOf(id));
      touchSession(id, room);
      history.replaceState(null, "", sessionAddress(id, room));
    } else {
      open(id, null);
    }
    runAllLines();
  };
  const example = async () => play(await randomExample());
  const pick = async file => play(await exampleCode(file));
  document.querySelector("#live-room .live-room-example")?.addEventListener("click", example);
  const closeExamples = initExampleList(pick);
  const closeScenes = initSceneList();
  // the keys, over the code, as Ctrl+Shift+H shows them
  document.querySelector("#live-room .live-room-keys")?.addEventListener("click", () => toggleLiveKeys());
  const room = document.getElementById("live-room");
  initCornerPopups([
    [".live-room-examples", () => closeExamples?.()],
    [".live-room-scenes", () => closeScenes?.()],
    [".live-room-toggle", () => room?.classList.remove("open")],
    [".live-room-keys", () => { if (isLiveKeysOpen()) toggleLiveKeys(false); }],
  ]);
  initHome();

  initLanding({ rooms, open, asked, mediaDir, example, pick, listExamples });

  // another session's address typed into this tab: open that one
  window.addEventListener("hashchange", () => location.reload());
});
